import { round } from "./helpers";

/**
 * A wrapper around Element.animate(). The function waits for animations to
 * finish and includes a basic polyfill for legacy browsers.
 * @param {Element} el The element to animate.
 * @param {object} options Arguments for Element.animate().
 * @param {Keyframe[]} options.keyframes The animation keyframes.
 * @param {KeyframeAnimationOptions} options.options the animation options.
 * @returns {Promise<void>}
 */
export async function animate(el, { keyframes, options }) {
  if (el instanceof Element) {
    if (typeof el.animate === "function") {
      const properties = Array.from(new Set(keyframes.flatMap(Object.keys)));
      el.style.willChange = properties.join(", ");
      await el.animate(keyframes, options).finished;
      el.style.willChange = null;
    } else {
      keyframes.forEach((obj) => {
        Object.assign(el.style, obj);
      });
    }
  }
}

/**
 * A wrapper around Element.replaceChildren() that's safe for legacy browsers.
 * @param {Node} el The parent element
 * @param {...(Node|string)} children New child elements
 */
export function replaceChildren(el, ...children) {
  if (el instanceof Element && (typeof "replaceChildren") in el) {
    el.replaceChildren(...children);
  }
  if (el instanceof Node) {
    while (el.firstChild) {
      el.removeChild(el.firstChild);
    }
    for (let child of children) {
      const node =
        typeof child === "string" ? document.createTextNode(child) : child;
      if (node instanceof Node) {
        el.appendChild(node);
      }
    }
  }
}

/**
 * Animates an array of elements in a staggered sequence.
 *
 * Use `options.duration` and `options.fac` to fine-tune the timing.
 * @param {Element[] | HTMLCollection | NodeList} elements Elements to animate
 * @param {object} options Animation configuration options
 * @param {number} options.duration The base animation duration
 * @param {number} options.fac A multiplier affecting the force of the animation
 * @param {Keyframe[]} options.keyframes Animation keyframes
 * @returns {Promise<void>} Returns a promise that resolves when all animations have settled
 */
export async function trail(elements, options = {}) {
  const defaults = {
    duration: 200,
    fac: 25,
    keyframes: [],
  };

  const o = Object.assign(defaults, options);

  return await Promise.all(
    Array.from(elements).map(async (el, index, arr) => {
      const { duration, fac } = o;
      const total = arr.length;
      const normal = index / total;
      return animate(el, {
        keyframes:
          typeof o.keyframes === "function"
            ? o.keyframes(el, normal, arr)
            : o.keyframes,
        options: {
          delay: round((duration / total) * normal * fac),
          duration: round(duration + (duration / total) ** normal * fac),
          easing: `cubic-bezier(0, 0, ${round(1 - normal, 2)}, 1)`,
          fill: "forwards",
        },
      });
    }),
  );
}
